import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

export type CoverageCellStatus = "full" | "missing";

export type CoverageStageCell =
  | { status: CoverageCellStatus; months?: never }
  | { months: CoverageCellStatus[]; status?: never };

export interface CoverageStageRow {
  id: string;
  type: string;
  basis: string;
  cells: CoverageStageCell[];
}

export interface CoverageStageGap {
  id: string;
  type: string;
  periods: string[];
}

interface CoverageStageProps {
  periods: string[];
  rows: CoverageStageRow[];
  additionalRows?: CoverageStageRow[];
  hasRfi: boolean;
  criticalGaps?: CoverageStageGap[];
  onBack: () => void;
  onProceed: () => void;
  onCreateRfi?: () => void;
  onUploadRfi?: () => void;
  backLabel?: string;
  proceedLabel?: string;
}

const UI_FONT = {
  fontFamily:
    "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
};

const CELL_STYLE: Record<CoverageCellStatus, string> = {
  full: "text-foreground",
  missing: "text-muted-foreground/50",
};

const CELL_MARK: Record<CoverageCellStatus, string> = {
  full: "●",
  missing: "—",
};

function CoverageRow({
  row,
  periods,
}: {
  row: CoverageStageRow;
  periods: string[];
}) {
  return (
    <tr className="border-b border-border/40 last:border-b-0">
      <td className="px-3 py-[5px]">{row.type}</td>
      <td className="px-3 py-[3px]">
        <span
          className="inline-block rounded-full border border-border bg-muted px-2 py-0.5 font-medium text-muted-foreground"
          style={{ fontSize: 9.5 }}
        >
          {row.basis}
        </span>
      </td>
      {periods.map((period, periodIndex) => {
        const cell = row.cells[periodIndex] ?? { status: "missing" as const };
        return (
          <td key={period} className="px-3 py-[3px] text-center">
            {cell.months ? (
              <span
                className="inline-flex items-center gap-[1.5px]"
                title={
                  cell.months.every((status) => status === "full")
                    ? "All 12 months received"
                    : cell.months.every((status) => status === "missing")
                      ? "No months received"
                      : "Some months missing"
                }
              >
                {cell.months.map((status, monthIndex) => (
                  <span
                    key={monthIndex}
                    className={
                      status === "full"
                        ? "bg-foreground"
                        : "bg-muted"
                    }
                    style={{ width: 5, height: 12, borderRadius: 1.5 }}
                  />
                ))}
              </span>
            ) : (
              <span
                className={`inline-flex w-full items-center ${cell.status === "full" ? "justify-end" : "justify-center"} font-semibold ${CELL_STYLE[cell.status]}`}
                title={cell.status}
              >
                {CELL_MARK[cell.status]}
              </span>
            )}
          </td>
        );
      })}
    </tr>
  );
}

export function CoverageStage({
  periods,
  rows,
  additionalRows = [],
  hasRfi,
  criticalGaps = [],
  onBack,
  onProceed,
  onCreateRfi,
  onUploadRfi,
  backLabel = "← Back to upload",
  proceedLabel = "Continue anyway →",
}: CoverageStageProps) {
  const [gridExpanded, setGridExpanded] = useState(true);
  const [rfiMenuOpen, setRfiMenuOpen] = useState(false);
  const displayedRows = [
    ...rows,
    ...(gridExpanded ? additionalRows : []),
  ];

  const rfiAction = onCreateRfi || onUploadRfi ? (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={() => setRfiMenuOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={rfiMenuOpen}
        className="flex items-center gap-2 whitespace-nowrap rounded-lg bg-foreground px-4 py-2 font-medium text-background hover:opacity-90"
        style={{ fontSize: 12.5 }}
      >
        Update RFI
        <ChevronDown size={13} aria-hidden="true" />
      </button>
      {rfiMenuOpen && (
        <div role="menu" className="absolute right-0 top-full z-20 mt-2 min-w-40 overflow-hidden rounded-lg border border-border bg-card p-1 shadow-xl">
          {onCreateRfi && (
            <button type="button" role="menuitem" onClick={() => { setRfiMenuOpen(false); onCreateRfi(); }} className="block w-full rounded-md px-3 py-2 text-left font-medium text-foreground hover:bg-muted" style={{ fontSize: 12 }}>
              Create
            </button>
          )}
          {onUploadRfi && (
            <button type="button" role="menuitem" onClick={() => { setRfiMenuOpen(false); onUploadRfi(); }} className="block w-full rounded-md px-3 py-2 text-left font-medium text-foreground hover:bg-muted" style={{ fontSize: 12 }}>
              Upload
            </button>
          )}
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className="flex-1 overflow-y-auto" style={UI_FONT}>
      <div className="builder-stage-frame pb-10 pt-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div className="font-semibold" style={{ fontSize: 20 }}>
            Update RFI
          </div>
          {criticalGaps.length === 0 && rfiAction}
        </div>

        {criticalGaps.length > 0 && (
          <div className="mb-5 flex flex-col gap-3 overflow-visible rounded-xl border border-border bg-card sm:flex-row sm:items-center">
            <div className="min-w-0 flex-1">
              <div className="px-4 py-3">
                <div className="font-semibold" style={{ fontSize: 13 }}>
                  We recommend requesting additional information because critical information is missing:
                </div>
              </div>
              <div className="px-4 py-2.5 font-medium text-foreground" style={{ fontSize: 11.5 }}>
                {criticalGaps.map((gap, index) => (
                  <span key={gap.id}>
                    {index > 0 && "; "}
                    {gap.type} for {gap.periods.join(", ")}
                  </span>
                ))}
              </div>
            </div>
            {rfiAction && (
              <div className="shrink-0 px-4 pb-3 sm:py-3 sm:pl-0">
                {rfiAction}
              </div>
            )}
          </div>
        )}

        <div className="mb-5 bg-card">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" style={{ fontSize: 11.5 }}>
              <thead>
                <tr className="border-b border-border">
                  <th
                    className="px-3 py-2 text-left font-semibold"
                    style={{ minWidth: 160 }}
                  >
                    File type
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">Basis</th>
                  {periods.map((period) => (
                    <th
                      key={period}
                      className="px-3 py-2 text-center font-semibold"
                    >
                      {period}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedRows.map((row) => (
                  <CoverageRow key={row.id} row={row} periods={periods} />
                ))}
              </tbody>
            </table>
          </div>
          {displayedRows.length === 0 && (
            <div
              className="px-4 py-8 text-center text-muted-foreground"
              style={{ fontSize: 11.5 }}
            >
              No recognized source categories were found.
            </div>
          )}
          <div
            className="flex items-center gap-4 border-t border-border/60 px-3 py-2 text-muted-foreground"
            style={{ fontSize: 10 }}
          >
            <span><span className="text-foreground">●</span> Year</span>
            <span className="inline-flex items-center gap-1">
              <span
                className="bg-foreground"
                style={{ width: 4, height: 10, borderRadius: 1 }}
              />
              Month
            </span>
            {additionalRows.length > 0 && (
              <button
                type="button"
                aria-expanded={gridExpanded}
                onClick={() => setGridExpanded((expanded) => !expanded)}
                className="ml-auto flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-muted-foreground hover:border-foreground hover:text-foreground"
                style={{ fontSize: 10 }}
                title={
                  gridExpanded
                    ? "Hide the detailed source types"
                    : "Show the detailed source types"
                }
              >
                {gridExpanded
                  ? <ChevronUp size={11} />
                  : <ChevronDown size={11} />}
                {gridExpanded
                  ? "Show fewer"
                  : `Show ${additionalRows.length} more`}
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <button
            onClick={onBack}
            className="rounded-lg border border-border px-4 py-2 font-medium text-foreground/80 hover:bg-muted"
            style={{ fontSize: 12.5 }}
          >
            {backLabel}
          </button>
          <button
            onClick={onProceed}
            className="rounded-lg border border-border px-4 py-2 font-medium text-foreground/80 hover:bg-muted"
            style={{ fontSize: 12.5 }}
          >
            {proceedLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
